import apiClient from '../client'
import type { Activity, ActivityFilters, PaginatedResponse } from '@/types'

// Backend response type
interface BackendActivitiesResponse {
  activities: Activity[]
  total: number
  limit: number
  offset: number
}

// Transform backend response to frontend PaginatedResponse format
const transformActivitiesResponse = (
  backendData: BackendActivitiesResponse,
  page: number,
  pageSize: number
): PaginatedResponse<Activity> => ({
  items: backendData.activities || [],
  total: backendData.total || 0,
  page: page,
  page_size: pageSize,
  total_pages: Math.ceil((backendData.total || 0) / pageSize),
})

export const activitiesApi = {
  getAll: async (filters: ActivityFilters = {}) => {
    const page = filters.page || 1
    const pageSize = filters.page_size || 25
    const offset = (page - 1) * pageSize

    const response = await apiClient.get<BackendActivitiesResponse>('/api/dashboard/activities/recent', {
      params: {
        ...filters,
        limit: pageSize,
        offset: offset,
      },
    })

    return {
      ...response,
      data: transformActivitiesResponse(response.data, page, pageSize),
    }
  },

  getById: (id: string) =>
    apiClient.get<Activity>(`/api/dashboard/activities/${id}`),

  getByLead: async (leadId: string, filters: ActivityFilters = {}) => {
    const page = filters.page || 1
    const pageSize = filters.page_size || 25
    const offset = (page - 1) * pageSize

    const response = await apiClient.get<BackendActivitiesResponse>(`/api/dashboard/leads/${leadId}/activities`, {
      params: {
        ...filters,
        limit: pageSize,
        offset: offset,
      },
    })

    return {
      ...response,
      data: transformActivitiesResponse(response.data, page, pageSize),
    }
  },

  create: (data: { lead_id: string; type: string; description: string; metadata?: Record<string, unknown> }) =>
    apiClient.post<Activity>('/api/dashboard/activities', data),
}
